import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, FormField, Input } from '@/components/ui';
import { getErrorMessage } from '@/api';
import { useSetPositionSurcharge } from '@/hooks/useAdminProducts';

interface PositionSurchargeFieldProps {
  productId: string;
  positionId: string;
  /** Current value as returned by the API — a decimal string, or null when unpriced. */
  value: string | null | undefined;
}

/**
 * Direct Sale surcharge for one position.
 *
 * An empty field means unpriced: the position disappears from the Direct Sale
 * flow while staying fully usable in the Seller flow. That is deliberately
 * different from entering 0, which offers the decoration free of charge — so
 * the two states are shown with distinct wording rather than one blank input.
 */
export function PositionSurchargeField({
  productId,
  positionId,
  value,
}: PositionSurchargeFieldProps) {
  const toInput = (v: string | null | undefined) =>
    v === null || v === undefined ? '' : Number(v).toFixed(2);

  const [draft, setDraft] = useState(() => toInput(value));
  const { mutateAsync, isPending } = useSetPositionSurcharge(productId);

  // Reset the field when switching to another position, otherwise the
  // previous position's amount would linger in the input.
  useEffect(() => {
    setDraft(toInput(value));
  }, [positionId, value]);

  const persisted = toInput(value);
  const isDirty = draft.trim() !== persisted;
  const parsed = draft.trim() === '' ? null : Number(draft);
  const isValid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);

  const handleSave = async () => {
    if (!isValid) return;
    try {
      await mutateAsync({ positionId, printSurcharge: parsed });
      toast.success(
        parsed === null
          ? 'Position removed from Direct Sale'
          : 'Surcharge saved'
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const inputId = `surcharge-${positionId}`;

  return (
    <FormField
      label="Direct Sale surcharge"
      htmlFor={inputId}
      error={isValid ? undefined : 'Enter 0 or a positive amount.'}
      hint={
        parsed === null
          ? 'Empty — this position is not offered in Direct Sale.'
          : parsed === 0
            ? 'Free of charge, but offered in Direct Sale.'
            : 'Added on top of the product price per decorated position.'
      }
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            id={inputId}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={draft}
            placeholder="not offered"
            onChange={(e) => setDraft(e.target.value)}
            className="pr-8"
          />
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-mute">
            €
          </span>
        </div>
        <Button
          onClick={handleSave}
          disabled={!isDirty || !isValid || isPending}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </FormField>
  );
}
